import React, { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import toast from 'react-hot-toast';
import { Mail, Lock, LogIn } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { AuthShell, AuthCard, authInputClass } from '../components/Common/AuthShell';

export const Login: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login, isLoading } = useAuthStore();
  const { t } = useTranslation('auth');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email || !password) {
      toast.error(t('common.fillAllFields'));
      return;
    }

    try {
      await login(email, password);
      toast.success(t('login.welcomeBack'));
      // Honour the `?redirect=` hint that the global 401 interceptor
      // sets when it bounces an expired session here. Only allow
      // same-origin paths to avoid an open-redirect bug.
      const redirect = searchParams.get('redirect');
      const safeRedirect =
        redirect && redirect.startsWith('/') && !redirect.startsWith('//')
          ? redirect
          : '/dashboard';
      navigate(safeRedirect);
    } catch (error: any) {
      console.error('Login error:', error);
      toast.error(error.message || t('login.loginFailed'));
    }
  };

  return (
    <AuthShell tagline={t('login.tagline')} back={{ label: t('common.backToHome'), to: '/' }}>
      <AuthCard>
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Email Input */}
          <div>
            <label htmlFor="email" className="mb-2 block text-sm font-semibold text-white/80">
              {t('common.emailAddress')}
            </label>
            <div className="relative">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5">
                <Mail className="h-5 w-5 text-white/40" />
              </div>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={authInputClass}
                placeholder={t('common.emailPlaceholder')}
                disabled={isLoading}
              />
            </div>
          </div>

          {/* Password Input */}
          <div>
            <label htmlFor="password" className="mb-2 block text-sm font-semibold text-white/80">
              {t('common.password')}
            </label>
            <div className="relative">
              <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5">
                <Lock className="h-5 w-5 text-white/40" />
              </div>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={authInputClass}
                placeholder={t('common.passwordPlaceholder')}
                disabled={isLoading}
              />
            </div>
          </div>

          {/* Forgot Password Link */}
          <div className="flex items-center justify-end">
            <Link to="/forgot-password" className="text-sm font-semibold text-cyan-200 transition-colors hover:text-cyan-100">
              {t('login.forgotPassword')}
            </Link>
          </div>

          {/* Submit Button */}
          <button type="submit" disabled={isLoading} className="landing-primary-cta w-full disabled:cursor-not-allowed disabled:opacity-60">
            {isLoading ? (
              <>
                <svg className="-ml-1 mr-1 h-5 w-5 animate-spin text-growth-ink" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                {t('login.submitting')}
              </>
            ) : (
              <>
                <LogIn className="h-5 w-5" />
                {t('login.submit')}
              </>
            )}
          </button>
        </form>

        {/* Register Link */}
        <div className="mt-8">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-white/10" />
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="bg-transparent px-4 text-white/[0.5] [backdrop-filter:blur(8px)]">
                {t('login.noAccount')}
              </span>
            </div>
          </div>

          <div className="mt-6">
            <Link
              to="/register"
              className="flex w-full items-center justify-center rounded-2xl border border-white/14 bg-white/[0.06] px-4 py-3 text-base font-bold text-white transition-all hover:-translate-y-0.5 hover:bg-white/[0.1]"
            >
              {t('login.createAccount')}
            </Link>
          </div>
        </div>
      </AuthCard>
    </AuthShell>
  );
};
